import { adoptStyles } from '@squadlab/kernel/adopt-styles';

/**
 * The departure half of the warp convention (`docs/warp-transition.md`), for a
 * non-ship same-tab hop: a black curtain fades in over the page, then the browser
 * navigates. The Command Center already implements the matching *arrival*, so a
 * return to the bridge reads as the same continuous flight the outbound warp does.
 *
 * This used to be a copy-paste snippet in that doc. ADR 0110 makes it shared code:
 * ten hand-copied curtains would be ten slightly different curtains.
 *
 * Every effect is injectable so the behaviour is testable without a real
 * navigation, and the whole thing is a no-op under `prefers-reduced-motion`.
 */

export const WARP_CURTAIN_CLASS = 'squadlab-warp-curtain';

/** ms the curtain takes to cover the page before the browser leaves. */
const CURTAIN_MS = 420;

const CURTAIN_CSS = `
.${WARP_CURTAIN_CLASS} {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  background: #010206;
  opacity: 0;
  pointer-events: none;
  transition: opacity ${CURTAIN_MS}ms ease-in;
}
.${WARP_CURTAIN_CLASS}.is-in { opacity: 1; }
@media (prefers-reduced-motion: reduce) {
  .${WARP_CURTAIN_CLASS} { display: none; }
}
`;

export interface WarpDepartDeps {
  /** Perform the navigation. Defaults to a same-tab `location.assign`. */
  readonly navigate?: (href: string) => void;
  /** Skip the animation entirely. Defaults to the user's `prefers-reduced-motion`. */
  readonly reducedMotion?: boolean;
  /** Defer a beat. Defaults to `setTimeout`. */
  readonly schedule?: (fn: () => void, ms: number) => void;
  /**
   * Wait for the browser's next rendering opportunity. Defaults to
   * `requestAnimationFrame`. This is NOT interchangeable with `schedule`: a
   * `setTimeout(fn, 0)` runs *before* the next paint, so the curtain's
   * `opacity: 0` is never committed and no transition is ever created — the page
   * cuts straight to black. Measured in Chromium: with `setTimeout(fn, 0)` the
   * element reports `getAnimations().length === 0` and opacity 1 on the first
   * frame; with a rAF it animates. The Command Center's own departure uses a rAF
   * for the same reason (`artifact/app.js`).
   */
  readonly nextFrame?: (fn: () => void) => void;
  /** Where the curtain is appended. Defaults to `document.body`. */
  readonly root?: HTMLElement;
}

/**
 * The flag `docs/warp-transition.md` puts on the destination so it knows to boot
 * from black and fly its mark in, rather than running its ordinary splash. Without
 * it the departure fades out and the arrival never plays — half a flight.
 */
export const WARP_FLAG = 'warp';

/** Add `?warp=1` to a destination, preserving any query it already carries. */
export function withWarpFlag(href: string): string {
  try {
    // No base on purpose: the catalogue only ever yields absolute URLs, and resolving
    // against the current page would quietly turn a malformed destination into a
    // plausible same-origin one instead of leaving it alone.
    const url = new URL(href);
    url.searchParams.set(WARP_FLAG, '1');
    return url.toString();
  } catch {
    // A destination we cannot parse is still worth navigating to: losing the
    // arrival animation beats losing the way home.
    return href;
  }
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/** True once a departure is in flight, so a double-click can't queue two navigations. */
let departing = false;
/** The curtain currently covering the page, if any — so a restored page can clear it. */
let liveCurtain: HTMLElement | null = null;

/**
 * Abandon an in-flight departure: drop the curtain and release the latch.
 *
 * Normally a departure ends when the document is replaced, so nothing has to undo
 * it. But a page can come back: the back/forward cache restores this exact document,
 * with the opaque curtain still painted at `z-index: 2147483000` and the latch still
 * set — a page that looks hung and can never leave again. That is the silent failure
 * AGENTS.md forbids, so a restore clears it (and it doubles as the test seam).
 */
export function resetWarpDepart(): void {
  departing = false;
  liveCurtain?.remove();
  liveCurtain = null;
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  // `persisted` marks a bfcache restore specifically; a normal load has nothing to undo.
  window.addEventListener('pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) resetWarpDepart();
  });
}

/**
 * Raise the curtain, then navigate. Safe to call more than once: the second call
 * is ignored while the first is still in flight.
 */
export function warpDepart(href: string, deps: WarpDepartDeps = {}): void {
  if (departing) return;
  departing = true;

  const navigate = deps.navigate ?? ((to: string) => window.location.assign(to));
  const destination = withWarpFlag(href);
  const reduced = deps.reducedMotion ?? prefersReducedMotion();
  if (reduced) {
    navigate(destination);
    return;
  }

  const schedule = deps.schedule ?? ((fn: () => void, ms: number) => void setTimeout(fn, ms));
  const nextFrame =
    deps.nextFrame ??
    ((fn: () => void) => {
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => fn());
      else fn();
    });
  const root = deps.root ?? document.body;

  // Constructable stylesheet, not an inline `style` attribute: prod runs a strict
  // CSP with no 'unsafe-inline' (ADR 0007), and CSSOM is not governed by style-src.
  adoptStyles(document, CURTAIN_CSS);

  const curtain = document.createElement('div');
  curtain.className = WARP_CURTAIN_CLASS;
  curtain.setAttribute('aria-hidden', 'true');
  root.append(curtain);
  liveCurtain = curtain;

  // The class flip must land on a LATER frame than the append, or the browser never
  // commits `opacity: 0` and there is no transition to run. See `nextFrame` above.
  nextFrame(() => curtain.classList.add('is-in'));
  schedule(() => navigate(destination), CURTAIN_MS);
}
